<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { defineProps, defineEmits, computed } from "vue";
import PageHeader from "../PageHeader/PageHeader.vue";
import { Project } from "@/types";
import { useTranslate } from "@/composables/useTranslate";

const props = defineProps<Props>();

type Props = {
  projectDetails?: Project | null;
  phaseDetails?: any | null;
  appointmentProject?: any | null;
  appointmentDetails?: any | null;
  exampleArchive?: any | null;
  salesforceLink?: string | null;
  hasBaumappe?: boolean;
  isBaumappeOpen?: boolean;
  isInstallers?: boolean;
  isMonthlyBonus?: boolean;
  isInstallerProfile?: boolean;
  selectedWorkerProfile?: any;
  isInstallerReport?: boolean;
  bonusReports?: Array<any>;
};

defineEmits<Emits>();
type Emits = {
  (event: "toggleIsAddImageModalOpen", isOpen: boolean): void;
  (event: "toggleBaumappe", isOpen: boolean): void;
};

const route = useRoute();
const router = useRouter();
const { t } = useTranslate();
const path = route.path;

const goBack = () => {
  router.go(-1);
};

const goForward = () => {
  router.go(+1);
};

const selectedReportMonthFromURL = computed(() => {
  return props.bonusReports?.find((item: any) => {
    return item.id === Number(route.params.reportId);
  });
});
</script>

<template>
  <PageHeader>
    <template v-slot:header-left>
      <div class="flex items-center gap-1">
        <!-- Back / Forward buttons -->
        <div v-if="route.path !== '/'" class="flex items-center gap">
          <button
            @click="goBack"
            class="w-6 h-6 flex items-center justify-center cursor-pointer rounded-sm hover:bg-gray-200 duration-200"
          >
            <img src="@/assets/arrow-left.svg" alt="" />
          </button>
          <button
            @click="goForward"
            class="w-6 h-6 flex items-center justify-center cursor-pointer rounded-sm hover:bg-gray-200 duration-300"
          >
            <img
              v-if="
                !path.includes('projects') &&
                !path.includes('build') &&
                !path.includes('monthly-bonus')
              "
              src="@/assets/arrow-right.svg"
              alt="Arrow"
            />
            <img v-else src="@/assets/arrow-right-gray.svg" alt="Arrow" />
          </button>
        </div>
        <!-- Breadcrumb -->
        <div
          v-if="props.projectDetails"
          class="flex items-center gap-2 text-sm font-medium tracking-[0.01rem] text-[#898790]"
        >
          <RouterLink to="/"> {{ t("global.all_projects") }} </RouterLink>
          <span>/</span>
          <RouterLink
            :class="
              route.path.includes(String(route.params.id)) ? 'text-black' : ''
            "
            :to="`/projects/${route.params.id}`"
          >
            {{ projectDetails?.name }}
          </RouterLink>
        </div>
        <div
          class="flex items-center gap-2 text-sm font-medium tracking-[0.01rem] text-[#898790]"
          v-else-if="props.phaseDetails"
        >
          <RouterLink to="/"> All Projects </RouterLink>
          <span>/</span>
          <RouterLink :to="`/projects/${route.params.id}`">
            {{ phaseDetails?.project.name }}
          </RouterLink>
          <span>/</span>
          <RouterLink
            :to="`/projects/${phaseDetails?.project.name}/phase/${phaseDetails.phaseId}`"
            :class="
              route.path.includes(String(route.params.phaseId))
                ? 'text-black'
                : ''
            "
          >
            {{
              phaseDetails?.phases.find(
                (item: any) => String(item.phaseId) === route.params.phaseId
              )?.phase
            }}
          </RouterLink>
        </div>
        <div
          v-else-if="props.appointmentProject"
          class="flex items-center gap-2 text-sm font-medium tracking-[0.01rem] text-[#898790]"
        >
          <RouterLink to="/"> {{ t("global.all_projects") }} </RouterLink>
          <span>/</span>
          <RouterLink :to="`/projects/${appointmentProject?.id}`">
            {{ appointmentProject?.name }}
          </RouterLink>
          <span>/</span>
          <span>{{ t("global.termins") }}</span>
          <span>/</span>
          <span
            :class="
              route.path.includes(String(route.params.terminId))
                ? 'text-black'
                : ''
            "
          >
            {{ appointmentDetails.name ? appointmentDetails.name : "-" }}
          </span>
        </div>
        <!-- All monthly bonus -->
        <div v-if="isMonthlyBonus">
          <span
            class="text-primary-text-color font-medium text-sm tracking-[0.01rem]"
            >{{ t("installers.monthly_bonus") }}</span
          >
        </div>
        <!-- All installers -->
        <div v-if="props.isInstallers">
          <span
            class="text-primary-text-color font-medium text-sm tracking-[0.01rem]"
            >{{ t("installers.all_installers") }}</span
          >
        </div>
        <!-- Installer profile -->
        <div
          v-if="props.isInstallerProfile"
          class="flex items-center gap-2 text-sm font-medium tracking-[0.01rem] text-[#898790]"
        >
          <RouterLink to="/installers" class="underline">
            {{ t("installers.all_installers") }}
          </RouterLink>
          <span>/</span>
          <RouterLink
            :to="isInstallerReport ? `/installers/${route.params.id}` : ''"
            :class="
              isInstallerReport
                ? 'underline cursor-pointer text-[#898790]'
                : 'no-underline cursor-default text-primary-text-color'
            "
            >{{ props.selectedWorkerProfile?.fullname }}</RouterLink
          >
          <div v-if="isInstallerReport">
            <span>/</span>
            <span
              v-if="selectedReportMonthFromURL"
              class="text-primary-text-color ml-2 cursor-default"
              >{{ selectedReportMonthFromURL.month }}</span
            >
          </div>
        </div>
        <div
          class="flex items-center gap-2 text-sm font-medium tracking-[0.01rem] text-[#898790]"
          v-else-if="props.exampleArchive"
        >
          <span>{{ t("global.build") }}</span>
        </div>
      </div>
    </template>
    <template v-slot:header-right>
      <div class="flex items-center gap-4">
        <a v-if="salesforceLink" :href="salesforceLink" target="_blank">
          <img
            class="w-[16px] h-[16px] cursor-pointer"
            src="@/assets/salesforce.svg"
            alt=""
        /></a>
        <div v-if="phaseDetails" class="flex items-center gap-4">
          <button
            @click="$emit('toggleIsAddImageModalOpen', true)"
            class="flex items-center justify-center cursor-pointer"
          >
            <img src="@/assets/redesign-image-upload.svg" alt="Image" />
          </button>
          <button
            v-if="hasBaumappe"
            @click="$emit('toggleBaumappe', !isBaumappeOpen)"
            class="flex items-center justify-center cursor-pointer p-1 rounded-primary-radius"
            :class="`${isBaumappeOpen ? 'bg-white' : ''}`"
          >
            <img src="@/assets/redesign-map.svg" alt="Map" />
          </button>
        </div>
      </div>
    </template>
  </PageHeader>
</template>
